import { InternalStandardProps, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Layout } from "../Layout";
import { AppTypography } from "JS/React/Components/AppTypography";
import { useNavigate, useParams } from "react-router-dom";
import { useRouting } from "JS/React/Hooks/Routes";
import { AppHeader } from "JS/React/Components/AppHeader";
import { messaging } from "JS/Helpers/UserMessaging";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import { getUserOS, isIpad } from "JS/Helpers";
import { useEffect, useRef, useState } from "react";
import { config } from "JS/Config";
import { UploadImageDialog } from "JS/React/Components/EventsDialog/UploadImageDialog";
import { WebCamImageDailog } from "JS/React/Components/EventsDialog/WebCamImageDailog";
import { useFycStatusDetail } from "JS/React/Hooks/FycStatus/FycStatus";
import moment from "moment";
import { UserRoute } from "JS/Helpers/FycStatusConstants";
import { UserTabs } from "JS/React/Components/UserTabs";
import { UserImage } from "JS/React/Components/UserImage";
import { useImageUpload } from "JS/React/Hooks/Common/ImageUpload";
import { AppConfimationDialog } from "JS/React/Components/AppConfirmationDialog";
import { RenderIf } from "JS/React/Components/Wrapper/RenderIf";

export interface fycStatusProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

const StyledRoot = styled("div")({});

const StyledGreyA100Typography = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey.A100,
}));

const StyledRedTypography = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.error.main,
}));

const StyledTextContainer = styled(Box)({
    marginBottom: "15px",
});

const StyledMessageContainer = styled(Box)({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0 20px",
    width: "100%",
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
});

const StyledMessageTypography = styled(AppTypography)(({ theme }) => ({
    textAlign: "center",
    color: theme.palette.grey.A100,
}));

const os = getUserOS();

export const FycStatus = (props: fycStatusProps) => {
    const { className, ...rest } = props;
    const { homeStack } = useGlobalNavStack();
    const { linkProvider } = useRouting();
    const navigate = useNavigate();
    const {
        fycStatus,
        loading: fycStatusLoading,
        refetch: refetchFycStatus,
    } = useFycStatusDetail();
    const hdnFileInput = useRef(null);

    const params: any = useParams();
    const { selectedTab: activeTab } = params;

    const [backgroundImageUrl, setBackgroundImageUrl] = useState<string>("");
    const [dialogs, setDialogs] = useState({
        uploadImageDialog: false,
        webCamImageDialog: false,
    });
    const [isImageExists, setIsImageExists] = useState<boolean>(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const [selectedTab, setSelectedTab] = useState({
        isSecondary: false,
        userId: "",
    });

    const {
        uploadImageHandler,
        webCamImageHandler,
        captureUploadHandler,
        closeUploadDialogHandler,
        fetchImageUrl,
        uploadingImage,
        fetchingImage,
        imageChecked,
        imageFetchFailed,
    } = useImageUpload();

    useEffect(() => {
        if (activeTab === UserRoute.SECONDARY_USER) {
            setSelectedTab({
                isSecondary: true,
                userId: `${config?.user?.memberId}2`,
            });
        } else {
            setSelectedTab({
                isSecondary: false,
                userId: config?.user?.memberId,
            });
        }
    }, [activeTab]);

    useEffect(() => {
        if (fycStatus?.isComplimentarySubscriber && selectedTab?.userId) {
            fetchImageUrl(getUserId(), setBackgroundImageUrl);
        }
    }, [fycStatus, selectedTab?.userId]);

    useEffect(() => {
        // fetchImageUrl only sets a url when the api reports the image exists
        setIsImageExists(!!backgroundImageUrl);
    }, [backgroundImageUrl]);

    useEffect(() => {
        // a user who has never uploaded a photo must do so before viewing the fyc status
        if (imageChecked && !backgroundImageUrl) {
            setDialogs((prev) => ({ ...prev, uploadImageDialog: true }));
        }
    }, [imageChecked, backgroundImageUrl]);

    const handleCloseUploadDialog = () => {
        // ejects only when no photo was ever uploaded; a photo missing from s3 leaves isImageExists true
        closeUploadDialogHandler(
            setDialogs,
            isImageExists,
            linkProvider.react.more().index(),
        );
    };

    const handleUploadImage = (event) => {
        uploadImageHandler(event, getUserId()).then((response) => {
            if (response) {
                fetchImageUrl(getUserId(), setBackgroundImageUrl);
                setDialogs({ ...dialogs, uploadImageDialog: false });
            }
        });
    };

    const handleWebCamImage = () => {
        webCamImageHandler(setDialogs, dialogs);
    };

    const handleCaptureUpload = async () => {
        captureUploadHandler(
            setDialogs,
            dialogs,
            getUserId(),
            capturedImage,
        ).then((res) => {
            if (res) {
                setDialogs({
                    uploadImageDialog: false,
                    webCamImageDialog: false,
                });
            }
            fetchImageUrl(getUserId(), setBackgroundImageUrl);
            setCapturedImage(null);
        });
    };

    const handleUploadClick = () => {
        hdnFileInput.current.click();
    };

    const handleTabSwitch = (userId: string) => {
        navigate(
            linkProvider.react
                .fycStatus()
                .index(
                    userId === config?.user?.memberId
                        ? UserRoute.PRIMARY_USER
                        : UserRoute.SECONDARY_USER,
                ),
            { replace: true },
        );
        setSelectedTab({
            isSecondary: userId !== config?.user?.memberId,
            userId: userId,
        });
    };

    // nothing is known about the photo until the check resolves one way or the other
    const imageCheckPending = !imageChecked && !imageFetchFailed;

    const isDefaultBehaviour = isIpad ? false : os?.includes("Macintosh");

    const getUserId = () =>
        selectedTab?.isSecondary
            ? config.user.memberId + "2"
            : config.user.memberId;

    const getUserName = () =>
        selectedTab?.isSecondary
            ? `${fycStatus?.spouseFirstName} ${fycStatus?.spouseLastName}`
            : `${config?.user?.firstName} ${config?.user?.lastName}`;

    const isDateExpired = () => {
        const currentDateTime = new Date();
        const fycExpiryDate = new Date(Number(fycStatus?.expiryDate));

        return fycExpiryDate < currentDateTime;
    };

    const formatStringDate = (stringDate: string) =>
        stringDate
            ? moment(parseInt(stringDate, 10)).format("MM/DD/YY")
            : "---";

    const getFycExpiryDate = () =>
        `First Year Expire${isDateExpired() ? "d" : "s"} On: ${formatStringDate(
            fycStatus?.expiryDate,
        )}`;

    return (
        <Layout>
            <StyledRoot {...rest} className={className}>
                <AppHeader
                    title={"My First Year Status"}
                    canGoBack
                    searchable={false}
                    onBackClick={() => {
                        if (!homeStack?.isHomeTrack) {
                            navigate(linkProvider.react.more().index());
                        } else {
                            navigate(linkProvider.react.home());
                        }
                    }}
                />
                {!fycStatusLoading && !fycStatus && (
                    <AppConfimationDialog
                        open={true}
                        confirmText="Retry"
                        popupMainText={`${messaging?.fycStatus?.apiFailed}`}
                        onConfirm={() => {
                            refetchFycStatus();
                        }}
                        onClose={() => {
                            navigate(linkProvider.react.more().index());
                        }}
                        showConfirmFirst={true}
                        showButtonBackgroundColor={true}
                        showCloseIcon={true}
                        showCancelButton={false}
                    />
                )}
                {fycStatusLoading && (
                    <AppBackdropProgress
                        open={true}
                        backdropText={messaging?.fycStatus?.fetchingFycStatus}
                    />
                )}
                {!fycStatusLoading && fycStatus && (
                    <RenderIf
                        isTrue={fycStatus?.isComplimentarySubscriber}
                        fallback={
                            <StyledMessageContainer>
                                <StyledMessageTypography variant="h6">
                                    {
                                        messaging?.fycStatus
                                            ?.expiredComplimentaryPlan
                                    }
                                </StyledMessageTypography>
                            </StyledMessageContainer>
                        }
                    >
                        <>
                            <StyledTextContainer
                                display={"flex"}
                                alignItems={"center"}
                                flexDirection={"column"}
                            >
                                <Box>
                                    <StyledGreyA100Typography
                                        fontWeight={"bold"}
                                        variant="h4"
                                    >
                                        APPRENTICE
                                    </StyledGreyA100Typography>
                                </Box>

                                <Box>
                                    <StyledGreyA100Typography variant="h6">
                                        {`ID: ${getUserId()}`}
                                    </StyledGreyA100Typography>
                                </Box>

                                <Box>
                                    <StyledGreyA100Typography
                                        fontWeight={"bold"}
                                        variant="h5"
                                    >
                                        {getUserName()}
                                    </StyledGreyA100Typography>
                                </Box>
                                <Box>
                                    <StyledRedTypography
                                        fontWeight={"bold"}
                                        variant="h5"
                                    >
                                        {getFycExpiryDate()}
                                    </StyledRedTypography>
                                </Box>
                            </StyledTextContainer>

                            <UserTabs
                                isSecondaryActive={selectedTab?.isSecondary}
                                handleTabSwitch={handleTabSwitch}
                                showPrimaryUser={true}
                                hasSpouse={fycStatus?.hasSpouse}
                            />

                            <UserImage
                                isImageExists={isImageExists}
                                backgroundImageUrl={backgroundImageUrl}
                                handleUploadClick={handleUploadClick}
                                isDefaultBehaviour={isDefaultBehaviour}
                                setDialogs={setDialogs}
                                dialogs={dialogs}
                                handleUploadImage={handleUploadImage}
                                hdnFileInput={hdnFileInput}
                                fetchingImage={fetchingImage}
                                imageFetchFailed={imageFetchFailed}
                                imageCheckPending={imageCheckPending}
                                isFromFYC={true}
                            />
                        </>
                    </RenderIf>
                )}
                {!uploadingImage && (
                    <UploadImageDialog
                        open={dialogs?.uploadImageDialog}
                        isImageExists={isImageExists}
                        onClose={handleCloseUploadDialog}
                        onUploadImage={handleUploadImage}
                        onWebCamImage={handleWebCamImage}
                        uploadingImage={uploadingImage}
                    />
                )}
                <WebCamImageDailog
                    capturedImage={capturedImage}
                    setCapturedImage={setCapturedImage}
                    open={dialogs?.webCamImageDialog}
                    onClose={handleCloseUploadDialog}
                    onUploadCaptureImage={handleCaptureUpload}
                />

                {uploadingImage && (
                    <AppBackdropProgress
                        open={true}
                        backdropText={messaging?.events?.uploadingPhoto}
                    />
                )}

                {fetchingImage && (
                    <AppBackdropProgress
                        open={true}
                        backdropText={messaging?.events?.fetchingPhoto}
                    />
                )}
            </StyledRoot>
        </Layout>
    );
};
